import {
  Calendar,
  CaretLeft,
  CheckCircle,
  Clock,
  DotsThreeVertical,
  MapPin,
  LockSimple,
  QrCode,
  SignOut,
  Trash,
  UserPlus,
  Users,
  UsersThree,
} from '@phosphor-icons/react';
import type { LangueParlee, SiroccoEvent } from '@sirocco/shared';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  editableFor,
  events as eventsApi,
  messages as messagesApi,
  type Attendance,
} from '../../api/endpoints';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { Avatar } from '../../components/ui/Avatar';
import { Button, IconButton } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/Bits';
import { CatalogueDeLangues } from '../../components/ui/CatalogueDeLangues';
import { ChampDesLangues } from '../../components/ui/ChampDesLangues';
import { ConfirmDialog, useConfirm } from '../../components/ui/Confirm';
import { Field } from '../../components/ui/Field';
import { Sheet, SheetAction } from '../../components/ui/Sheet';
import { useAsync } from '../../hooks/useAsync';
import { useScanner } from '../../hooks/useScanner';
import { useSession } from '../../store/SessionContext';
import { shortDate, timeRange } from '../../utils/format';
import { lireBillet } from '../../utils/billet';
import { LANGUES_MAX_PAR_SORTIE } from '../../utils/langues';
import { t } from '../../i18n';

/**
 * Gestion d'un evenement, cote organisateur.
 *
 * Tout ce qu'on pilote depuis l'exterieur de la fiche publique : ce qui
 * se decide avant (le titre, le jour, les horaires, qui entre), et ce qui
 * se constate le jour meme (qui est venu).
 *
 * Le nombre de places n'est pas modifiable, volontairement : le reduire
 * mettrait dehors des gens deja acceptes, l'augmenter changerait la
 * nature de ce a quoi les autres se sont inscrits. Et rien ne se modifie
 * plus dans les dix dernieres heures — passe ce point, des gens ont pris
 * un train et prevenu quelqu'un.
 */
export function EventManageScreen() {
  const { eventId = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useSession();
  const { data, loading, error, reload, setData } = useAsync(
    () => eventsApi.get(eventId),
    [eventId],
    `event:${eventId}`,
  );

  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  /* Le bouton d'ou sort le menu de sortie. */
  const exitAnchor = useRef<HTMLButtonElement>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  /*
    Quelle tuile est ouverte, s'il y en a une.

    L'ecran montre d'abord ses quatre portes et rien d'autre : au jour de
    l'evenement, on cherche le scanner, pas une liste de trente noms a
    faire defiler pour l'atteindre.
  */
  /* Ce que le dernier code lu a donne, affiche par-dessus la camera : sans
     retour, on ne sait pas si le carre a ete pris. */
  const [scanEcho, setScanEcho] = useState<string | null>(null);

  /*
    La presence est lue a part de l'evenement : une liste de cartes n'en a
    que faire, et la poser sur le type doublerait les lectures partout.
  */
  const [presences, setPresences] = useState<Attendance>({});
  useEffect(() => {
    if (!eventId) return;
    let vivant = true;
    void eventsApi
      .attendance(eventId)
      .then((valeur) => vivant && setPresences(valeur))
      .catch(() => undefined);
    return () => {
      vivant = false;
    };
  }, [eventId]);
  /**
   * La feuille de presence, une fois arretee.
   *
   * Tant qu'elle ne l'est pas, aucune absence n'existe : un organisateur
   * qui oublie de pointer ne penalise personne. C'est ce qui rend le
   * rappel utile, et c'est aussi pourquoi le geste est irreversible —
   * une absence inscrite au casier de quelqu'un ne doit pas dependre
   * d'un second passage.
   */
  const [feuilleArretee, setFeuilleArretee] = useState<number | null>(null);
  useEffect(() => {
    if (!eventId) return;
    let vivant = true;
    void eventsApi
      .sheetClosedAt(eventId)
      .then((quand) => vivant && setFeuilleArretee(quand))
      .catch(() => undefined);
    return () => {
      vivant = false;
    };
  }, [eventId]);

  // La suppression efface l'evenement pour tout le monde, participants
  // compris : elle passe par une question.
  const { pending, request, cancel, confirm } = useConfirm();

  const isOrganizer = Boolean(data && user && data.organizer.id === user.id);
  const others = data?.participants.filter((member) => member.id !== user?.id) ?? [];
  // Un repreneur peut venir des confirmes comme des demandes en attente :
  // accepter puis transmettre en deux gestes serait inutilement long.
  const successors = [...others, ...(data?.pendingParticipants ?? [])];
  const presentCount = data?.participants.filter((membre) => presences[membre.id]).length ?? 0;

  /* Rien a valider : la porte des demandes n'a pas lieu d'etre. */
  const sansDemandes = !data || data.isPast || data.pendingParticipants.length === 0;

  /* Passe ce point, plus rien ne se modifie : des gens ont pris un train. */
  const modifiable = Boolean(data && !data.isPast && editableFor(data) > 0);
  /* La carte des infos est un bouton tant qu'elle s'ouvre, un bloc sinon. */
  const Carte = modifiable ? 'button' : 'div';

  /* Ceux qui seront comptes absents si la feuille est arretee maintenant.
     L'organisateur ne se compte jamais lui-meme. */
  const manquants =
    data?.participants.filter(
      (membre) => membre.id !== data.organizer.id && !presences[membre.id],
    ).length ?? 0;



  /**
   * Le groupe de l'evenement, en un appui.
   *
   * Il reunit les participants confirmes ; les demandes en attente n'en
   * font pas partie, sans quoi accepter reviendrait a une formalite.
   *
   * Une fois ouvert, il est retenu par l'evenement : le bouton y ramene au
   * lieu d'en creer un second, et la liste des participants et celle du
   * groupe ne se separent plus — valider quelqu'un l'y fait entrer, le
   * retirer l'en sort.
   */
  async function openGroup() {
    if (!data) return;
    if (data.groupId) {
      navigate(`/app/messages/${data.groupId}`);
      return;
    }
    setBusy('group');
    setNotice(null);
    try {
      const conversation = await messagesApi.createGroup(
        data.title,
        `${data.location} · ${shortDate(data.date)} · ${timeRange(data.startTime, data.endTime)}`,
        others.map((member) => member.id),
        null,
        /* Le groupe se sait celui de la sortie : c'est ce qui ferme sa
           porte aux invitations, ici comme dans les regles. */
        data.id,
      );
      await eventsApi.linkGroup(data, conversation.id);
      navigate(`/app/messages/${conversation.id}`);
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : t.sorties.gestion.groupeImpossible);
    } finally {
      setBusy(null);
    }
  }

  /**
   * Passer la main puis s'en aller.
   * Sans repreneur il n'y a rien a transmettre : l'evenement disparait,
   * ce qui vaut mieux qu'une sortie que plus personne ne peut organiser.
   */
  async function handOver(successorId: string) {
    if (!data) return;
    setLeaveOpen(false);
    setBusy('leave');
    try {
      await eventsApi.handOver(data, successorId);
      navigate('/app/events', { replace: true });
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : t.sorties.gestion.passationImpossible);
      setBusy(null);
    }
  }

  /**
   * Cocher ou decocher une presence.
   *
   * Le meme geste dans les deux sens : une erreur de scan se repare d'un
   * appui, et l'organisateur n'a pas a chercher ou l'annuler.
   */

  /**
   * Arreter la feuille de presence.
   *
   * Tout ce qui n'a pas ete pointe devient une absence. On annonce le
   * nombre avant de demander confirmation : valider une feuille sans
   * savoir combien de gens on marque absents serait signer a l'aveugle.
   */
  async function validerLaFeuille() {
    if (!data) return;
    setBusy('feuille');
    setNotice(null);
    try {
      const arretee = await eventsApi.closeSheet(data);
      setFeuilleArretee(arretee.closedAt);
      setNotice(
        arretee.absents === 0
          ? t.sorties.gestion.feuilleTousLa
          : t.sorties.gestion.feuilleAbsents(arretee.absents),
      );
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : t.sorties.gestion.validationImpossible);
    } finally {
      setBusy(null);
    }
  }

  /**
   * Un carre vient d'etre lu.
   *
   * Il coche tout de suite plutot que de demander confirmation : le but
   * est de fluidifier une file d'entree. Le nom s'affiche apres coup, et
   * la case se decoche d'un appui si ce n'etait pas la bonne personne.
   */
  async function onCode(texte: string) {
    if (!data) return;
    const billet = lireBillet(texte);
    if (!billet) return setScanEcho(t.sorties.gestion.scan.pasSirocco);
    if (billet.eventId !== data.id) return setScanEcho(t.sorties.gestion.scan.autreSortie);

    const membre = data.participants.find((personne) => personne.id === billet.userId);
    if (!membre) return setScanEcho(t.sorties.gestion.scan.pasInscrite);
    if (presences[billet.userId]) return setScanEcho(t.sorties.gestion.scan.dejaPresent(membre.username));

    try {
      setPresences(await eventsApi.markPresent(data, billet.userId, 'qr'));
      setScanEcho(t.sorties.gestion.scan.present(membre.username));
    } catch (cause) {
      setScanEcho(cause instanceof Error ? cause.message : t.sorties.gestion.scan.impossible);
    }
  }

  async function destroy() {
    if (!data) return;
    setLeaveOpen(false);
    setBusy('leave');
    try {
      await eventsApi.destroy(data);
      navigate('/app/events', { replace: true });
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : t.sorties.gestion.suppressionImpossible);
      setBusy(null);
    }
  }

  return (
    <SubScreen
      /* Sans titre : l'evenement se nomme deja en toutes lettres dans la
         carte qui ouvre l'ecran, et le redire au-dessus faisait deux fois
         la meme chose. La fleche dit d'ou l'on vient. */
      title=""
      action={
        /*
          Les deux sorties vivent dans un menu plutot qu'au bas de la
          liste. Elles y attendaient sous les participants, la ou l'on
          fait defiler pour cocher des presences : deux boutons dont l'un
          efface tout, atteints par le pouce en cherchant autre chose.
        */
        data && isOrganizer && !data.isPast ? (
          <IconButton
            label={t.sorties.commun.autresActions}
            variant="ghost"
            size={34}
            ref={exitAnchor}
            onClick={() => setExitOpen(true)}
          >
            <DotsThreeVertical size={22} weight="bold" />
          </IconButton>
        ) : undefined
      }
      overlay={
        data && (
          <>
            {/* Sans titre : on vient d'un ecran qui ne parle que de cet
                evenement-la, le rappeler ne leve aucun doute. */}
            <Sheet open={exitOpen} anchor={exitAnchor} onClose={() => setExitOpen(false)}>
              {/*
                Sans repreneur possible, seule la suppression a un sens :
                on ne propose pas de passer la main a personne.
              */}
              {successors.length > 0 && (
                <SheetAction
                  icon={<SignOut className="sens-x" size={22} />}
                  onClick={() => {
                    setExitOpen(false);
                    setLeaveOpen(true);
                  }}
                >
                  {t.sorties.gestion.quitter}
                </SheetAction>
              )}

              <SheetAction
                icon={<Trash size={22} />}
                tone="danger"
                onClick={() => {
                  setExitOpen(false);
                  request(t.sorties.gestion.supprimerQ, () => void destroy());
                }}
              >
                {t.sorties.gestion.supprimer}
              </SheetAction>
            </Sheet>

            <Sheet
              open={leaveOpen}
              onClose={() => setLeaveOpen(false)}
              title={t.sorties.gestion.aQui}
            >
              {successors.map((member) => (
                <SheetAction
                  key={member.id}
                  icon={<Avatar user={member} size={26} />}
                  onClick={() => {
                    setLeaveOpen(false);
                    request(
                      t.sorties.gestion.laisserQ(member.username),
                      () => void handOver(member.id),
                    );
                  }}
                >
                  {member.username}
                </SheetAction>
              ))}
            </Sheet>

            <Sheet
              open={editOpen}
              large
              onClose={() => setEditOpen(false)}
              title={t.sorties.gestion.modifier}
            >
              <EditForm
                event={data}
                onCancel={() => setEditOpen(false)}
                onSaved={(next) => {
                  setData(next);
                  setEditOpen(false);
                }}
              />
            </Sheet>

            <Sheet
              open={scanOpen}
              large
              onClose={() => {
                setScanOpen(false);
                setScanEcho(null);
              }}
              title={t.sorties.gestion.scanner}
            >
              <Scanner actif={scanOpen} echo={scanEcho} onCode={(t) => void onCode(t)} />
            </Sheet>

            <ConfirmDialog pending={pending} onCancel={cancel} onConfirm={confirm} />
          </>
        )
      }
    >
      {loading && <ScreenLoader />}
      {error && <ScreenError message={error} onRetry={reload} />}

      {data && !isOrganizer && (
        <EmptyState
          title={t.sorties.gestion.reserve}
          hint={t.sorties.gestion.reserveAide}
        />
      )}

      {/*
        L'ecran s'ouvre aussi pour un evenement passe.

        Il refusait tout, alors que **c'est apres coup** qu'on arrete sa
        feuille de presence : « Mes evenements » annoncait des feuilles a
        verifier et menait a un ecran vide. Ce qui n'a plus de sens une
        fois l'evenement termine est ecarte au cas par cas ci-dessous —
        modifier, accepter une demande, retirer quelqu'un — et ce qui en a
        reste : la liste, les presences, la validation.
      */}
      {data && isOrganizer && (
        <div className="ev-manage">
          {/*
            La carte entiere ouvre la modification, quand elle est encore
            ouverte : c'est son sujet, et une ligne « Modifier… » au bas
            d'un bloc qui ne parle que de cela etait un lien de trop.
          */}
          <Carte
            className={`card ev-manage__head${modifiable ? ' ev-manage__head--ouvrable' : ''}`}
            {...(modifiable
              ? { type: 'button' as const, onClick: () => setEditOpen(true) }
              : {})}
          >
            <span className="ev-manage__title">{data.title}</span>
            {/*
              Quatre faits, quatre reperes, chacun avec son icone — la
              meme grammaire que les cartes de la liste. Ils tenaient
              avant sur une seule ligne separee par des points : la date,
              l'horaire et le lieu s'y confondaient en une chaine qu'il
              fallait decouper des yeux.
            */}
            <div className="ev-manage__faits">
              <span className="ev-manage__fait">
                <Calendar size={16} />
                {shortDate(data.date)}
              </span>
              <span className="ev-manage__fait">
                <Clock size={16} />
                {timeRange(data.startTime, data.endTime)}
              </span>
              <span className="ev-manage__fait ev-manage__fait--seul">
                <MapPin size={16} />
                {data.location}
              </span>
            </div>

            {/*
              Dire pourquoi on ne peut plus, quand c'est le cas. Une
              carte qui cesse de repondre sans un mot laisse chercher.
            */}
            {!data.isPast && editableFor(data) <= 0 && (
              <span className="ev-manage__locked">
                <LockSimple size={15} />
                {t.sorties.gestion.tropTard}
              </span>
            )}
          </Carte>

          {notice && <p className="field__error">{notice}</p>}

          {/*
            Quatre portes, et rien d'autre a l'ecran tant qu'on n'en a
            pousse aucune.

            Le jour de l'evenement, on cherche le scanner — pas au bas
            d'une liste de trente noms. Les demandes et les participants
            vivaient l'un sous l'autre sur une seule colonne : il fallait
            traverser toute la premiere pour atteindre la seconde.

            La tuile des participants prend toute la largeur quand il n'y
            a rien a valider : une porte qui ne mene nulle part n'a pas a
            garder sa place.
          */}
          <div className="ev-tuiles">
              <button
                className="ev-tuile ev-tuile--geste"
                onClick={() => setScanOpen(true)}
              >
                <QrCode size={44} />
                <span className="ev-tuile__titre">{t.sorties.gestion.scannerUn}</span>
              </button>

              <button
                className="ev-tuile ev-tuile--geste"
                disabled={busy === 'group' || (!data.groupId && others.length === 0)}
                onClick={() => void openGroup()}
              >
                <UsersThree size={44} />
                <span className="ev-tuile__titre">
                  {data.groupId ? t.sorties.gestion.ouvrirGroupe : t.sorties.gestion.creerGroupe}
                </span>
                {others.length === 0 && (
                  <span className="ev-tuile__note">{t.sorties.gestion.personneAReunir}</span>
                )}
              </button>

              <button
                className={`ev-tuile${sansDemandes ? ' ev-tuile--pleine' : ''}`}
                onClick={() => navigate(`/app/events/${eventId}/participants`)}
              >
                <Users size={26} />
                <span className="ev-tuile__titre">{t.sorties.commun.participants}</span>
                <span className="ev-tuile__compte">
                  {data.isPast || feuilleArretee
                    ? `${presentCount} / ${data.participants.length} présents`
                    : `${data.participants.length} / ${data.maxParticipants}`}
                </span>
              </button>

              {!sansDemandes && (
                <button
                  className="ev-tuile"
                  onClick={() => navigate(`/app/events/${eventId}/demandes`)}
                >
                  <UserPlus size={26} />
                  <span className="ev-tuile__titre">{t.sorties.gestion.demandes}</span>
                  <span className="ev-tuile__compte">{data.pendingParticipants.length}</span>
                </button>
              )}
          </div>

          {/*
            La feuille de presence reste a la vue de tous, hors des
            tuiles : c'est le seul geste de cet ecran qui ne se rattrape
            pas, et le cacher derriere une porte le ferait oublier.
          */}
          {data.isPast &&
            (feuilleArretee ? (
              <p className="ev-manage__feuille ev-manage__feuille--faite">
                <CheckCircle size={17} weight="fill" />
                {t.sorties.gestion.valideeLe(shortDate(new Date(feuilleArretee).toISOString()))}
              </p>
            ) : (
              <div className="card ev-manage__feuille-rappel">
                <p className="ev-manage__feuille-titre">{t.sorties.gestion.feuilleAVerifier}</p>
                <p className="ev-manage__feuille-aide">
                  {t.sorties.gestion.feuilleAide}
                </p>
                <Button
                  variant="primary"
                  size="md"
                  block
                  loading={busy === 'feuille'}
                  disabled={busy !== null}
                  onClick={() =>
                    request(
                      manquants === 0
                        ? t.sorties.gestion.validerTousQ
                        : t.sorties.gestion.validerQ(manquants),
                      () => void validerLaFeuille(),
                    )
                  }
                >
                  {t.sorties.gestion.valider}
                </Button>
              </div>
            ))}
        </div>
      )}
    </SubScreen>
  );
}

/**
 * Ce qui reste modifiable une fois l'evenement publie.
 *
 * Les langues y sont, trois au plus : une sortie peut s'ouvrir a ceux
 * qu'on n'attendait pas, et c'est ce qui doit se dire avant qu'ils viennent.
 *
 * Le lieu et la description n'y sont pas : ils n'ont pas ete demandes, et
 * les ajouter reviendrait a decider a la place de quelqu'un. Le nombre de
 * places non plus, et pour une raison de fond — le baisser mettrait
 * dehors des gens deja acceptes.
 */
function EditForm({
  event,
  onCancel,
  onSaved,
}: {
  event: SiroccoEvent;
  onCancel: () => void;
  onSaved: (event: SiroccoEvent) => void;
}) {
  const [title, setTitle] = useState(event.title);
  const [date, setDate] = useState(event.date);
  const [startTime, setStartTime] = useState(event.startTime);
  const [endTime, setEndTime] = useState(event.endTime);
  const [langues, setLangues] = useState<LangueParlee[]>(event.langues);
  /* La liste des langues prend la place du formulaire, dans la meme feuille. */
  const [vue, setVue] = useState<'formulaire' | 'langues'>('formulaire');
  const [busy, setBusy] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const change =
    title !== event.title ||
    date !== event.date ||
    startTime !== event.startTime ||
    endTime !== event.endTime ||
    [...langues].sort().join() !== [...event.langues].sort().join();

  async function save() {
    setBusy(true);
    setErreur(null);
    try {
      onSaved(await eventsApi.update(event, { title, date, startTime, endTime, langues }));
    } catch (cause) {
      setErreur(cause instanceof Error ? cause.message : t.sorties.gestion.modificationImpossible);
      setBusy(false);
    }
  }

  if (vue === 'langues') {
    return (
      <div className="ev-filter__vue-langues">
        <div className="ev-filter__retour">
          <button
            type="button"
            className="sub-screen__back"
            aria-label={t.commun.retour}
            onClick={() => setVue('formulaire')}
          >
            <CaretLeft className="sens-x" size={22} weight="bold" />
          </button>
          <p className="ev-filter__head">{t.sorties.creation.languesTitre}</p>
        </div>
        <CatalogueDeLangues
          className="ev-filter__catalogue"
          valeur={langues}
          onChange={setLangues}
          auMoinsUne
          max={LANGUES_MAX_PAR_SORTIE}
        />
        <div className="ev-filter__actions">
          <Button size="md" onClick={() => setVue('formulaire')}>
            {t.sorties.creation.languesTermine}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="ev-edit">
      <Field label={t.sorties.gestion.champs.titre} tone="spot" value={title} onChange={(e) => setTitle(e.target.value)} />
      <Field
        label={t.sorties.commun.date}
        tone="spot"
        type="date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
      />
      <div className="ev-edit__times">
        <Field
          label={t.sorties.gestion.champs.debut}
          tone="spot"
          type="time"
          value={startTime}
          onChange={(e) => setStartTime(e.target.value)}
        />
        <Field
          label={t.sorties.gestion.champs.fin}
          tone="spot"
          type="time"
          value={endTime}
          onChange={(e) => setEndTime(e.target.value)}
        />
      </div>

      <ChampDesLangues
        label={t.sorties.creation.langues}
        valeur={langues}
        onChange={setLangues}
        onOuvrir={() => setVue('langues')}
      />

      <p className="ev-edit__note">
        {t.sorties.gestion.placesGardees}
      </p>

      {erreur && <p className="field__error">{erreur}</p>}

      <div className="ev-edit__actions">
        <Button variant="ghost" onClick={onCancel}>
          {t.commun.annuler}
        </Button>
        <Button loading={busy} disabled={!change} onClick={() => void save()}>
          {t.sorties.gestion.enregistrer}
        </Button>
      </div>
    </div>
  );
}

/**
 * La camera, et ce qu'elle vient de lire.
 *
 * Elle ne s'ouvre qu'avec la feuille et se referme avec elle : une camera
 * laissee ouverte allume la pastille de l'iPhone pour rien.
 */
function Scanner({
  actif,
  echo,
  onCode,
}: {
  actif: boolean;
  echo: string | null;
  onCode: (texte: string) => void;
}) {
  const { videoRef, erreur, pret } = useScanner(actif, onCode);

  return (
    <div className="ev-scan">
      {erreur ? (
        <p className="ev-scan__erreur">{erreur}</p>
      ) : (
        <>
          <div className="ev-scan__vue">
            <video ref={videoRef} className="ev-scan__video" autoPlay playsInline muted />
            {/* Une mire : elle dit ou viser, ce qu'un rectangle nu ne dit pas. */}
            <span className="ev-scan__mire" aria-hidden="true" />
          </div>
          {/*
            Rien sous l'image quand la camera est prete : la mire dit deja
            ou viser, et une consigne se lit une fois puis encombre. La
            ligne disparait entierement plutot que de rester vide — sans
            quoi elle garderait sa place et son interligne.
          */}
          {(echo ?? !pret) && (
            <p className="ev-scan__echo">{echo ?? t.sorties.gestion.ouvertureCamera}</p>
          )}
        </>
      )}
    </div>
  );
}
